import { defineStore } from 'pinia'
import { HttpError } from '@/helpers/errors'
import { SystemService } from '@/services/system/system'
import type {
  FAQItem,
  LatestVersionResponse,
  VersionReleaseNotes,
} from '@/types/services/system'

export interface SystemState {
  latest_version: {
    loading: boolean
    data: LatestVersionResponse | null
  }
  release_notes: {
    data: VersionReleaseNotes[]
    loading: boolean
    request_id: number
  }
  faq: {
    data: FAQItem[]
    loading: boolean
  }
}

export const useSystemStore = defineStore('system', {
  state(): SystemState {
    return {
      latest_version: {
        loading: true,
        data: null,
      },
      release_notes: {
        data: [],
        loading: true,
        request_id: 0,
      },
      faq: {
        data: [],
        loading: true,
      },
    }
  },

  actions: {
    async getLatestVersion() {
      this.$patch((state) => {
        state.latest_version.data = null
        state.latest_version.loading = true
      })

      try {
        const response = await SystemService.getLatestVersion()
        this.latest_version.data = response.data
      } catch (error) {
        throw new HttpError(error)
      } finally {
        this.latest_version.loading = false
      }
    },

    async getReleaseNotes(until_tag?: string | null) {
      const requestId = this.release_notes.request_id + 1
      this.$patch((state) => {
        state.release_notes.data = []
        state.release_notes.loading = true
        state.release_notes.request_id = requestId
      })

      try {
        const response = await SystemService.getReleaseNotes(until_tag)

        if (this.release_notes.request_id === requestId) {
          this.release_notes.data = response.data.data.sort((a, b) =>
            b.released_at.localeCompare(a.released_at)
          )
        }
      } catch (error) {
        throw new HttpError(error)
      } finally {
        if (this.release_notes.request_id === requestId) {
          this.release_notes.loading = false
        }
      }
    },

    async sendBugReport(name: string, description: string) {
      try {
        await SystemService.postBugReport(name, description)
      } catch (error) {
        throw new HttpError(error)
      }
    },

    async getFAQ() {
      this.$patch((state) => {
        state.faq.data = []
        state.faq.loading = true
      })

      try {
        const response = await SystemService.getFAQ()
        this.faq.data = response.data.data
      } catch (error) {
        throw new HttpError(error)
      } finally {
        this.faq.loading = false
      }
    },
  },
})
